/**
 * The console side of the CRM bridge (ADR 0020).
 *
 * The client CRM is mounted under this origin at /crm/ and shown in one
 * persistent iframe. The two sides talk over postMessage; every message
 * carries a `source` and is accepted only from this origin and from the
 * frame we created. The CRM side lives in crm/src/public/shared/console-embed.js.
 */

import type { VaultItem } from '../library/vault/vaultData';

export const CRM_FRAME_PATH = '/crm/';

/** Pages of the CRM's per-account Sidebar plus its global Dashboard. */
export const CRM_PAGES = [
  'dashboard',
  'home',
  'notifications',
  'chats',
  'collections',
  'vault',
  'queue',
  'statements',
  'statistics',
  'profile',
  'more',
  'banking',
  'mass',
  // Fansly pages reached from More (console React pages only).
  'plans',
  'tracking',
  'messaging',
  'management',
  'streaming',
  'settings',
  // OnlyFans pages reached from More (console React pages only).
  'referrals',
  'discover',
  'releaseforms',
  'cards',
] as const;

export type CrmPage = (typeof CRM_PAGES)[number];

/** AI → CRM opens the account's Messages, as the operator asked. */
export const DEFAULT_CRM_PAGE: CrmPage = 'chats';

export function isCrmPage(value: string | null | undefined): value is CrmPage {
  return typeof value === 'string' && (CRM_PAGES as readonly string[]).includes(value);
}

/** What the frame should show: an account page, or the global CRM dashboard. */
export type CrmTarget =
  | { accountId: string; page: CrmPage }
  | { accountId?: undefined; page: 'dashboard' };

export function sameTarget(a: CrmTarget | null, b: CrmTarget | null): boolean {
  if (!a || !b) return a === b;
  return (a.accountId ?? '') === (b.accountId ?? '') && a.page === b.page;
}

const CONSOLE_SOURCE = 'onlymind-console';
const CRM_SOURCE = 'onlymind-crm';

/** Media a frame page already has attached, as it hands it to the console's content picker. */
export interface FramePickedMedia {
  id: number;
  type: 'photo' | 'video' | 'audio' | 'gif';
  thumb: string;
}

/** A picked item as the frame pages read vault rows (OnlyFans' vault/media shape). */
export interface FrameVaultRow {
  id: number;
  type: string;
  duration: number;
  createdAt: string;
  files: Record<'thumb' | 'squarePreview' | 'full', { url: string }>;
}

export type ConsoleToCrm =
  | { type: 'crm:auth'; accessToken: string }
  | { type: 'crm:navigate'; accountId?: string; page: CrmPage; fanId?: string }
  | { type: 'crm:theme'; theme: string }
  /** The console notifies for chats (React Chats, ADR 0024): the frame stays quiet. */
  | { type: 'crm:quiet'; quiet: boolean }
  /** The console's content picker is open for this request: the frame does not open its own. */
  | { type: 'crm:content-picker-opened'; requestId: string }
  /** What was picked, in order; null = closed without attaching. */
  | { type: 'crm:content-picked'; requestId: string; items: FrameVaultRow[] | null };

export type CrmToConsole =
  | { type: 'crm:ready' }
  | { type: 'crm:location'; accountId: string; page: string }
  | { type: 'crm:workspace'; accountId: string; mode: string; message: string }
  | { type: 'crm:auth-expired'; reason: string }
  | { type: 'crm:unread'; accountId: string; count: number }
  | { type: 'crm:access-denied'; accountId: string }
  /** OnlyFans answered that the Account's session is logged out (or that it works again). */
  | { type: 'crm:account-session'; accountId: string; loggedOut: boolean }
  | { type: 'crm:no-access'; status: number }
  | { type: 'crm:signed-out' }
  /** A frame page (Mass messages, the old Chats) asks for the console's content picker. */
  | { type: 'crm:pick-content'; requestId: string; accountId: string; initial: FramePickedMedia[] };

/** The iframe URL for a first target; the CRM reads accountId/page at boot. */
export function crmFrameSrc(target: CrmTarget | null): string {
  // shellnav: the console draws the CRM page rail, so the CRM hides its Sidebar (ADR 0023).
  const params = new URLSearchParams({ embed: '1', shellnav: '1' });
  if (target?.accountId) params.set('accountId', target.accountId);
  params.set('page', target?.page ?? 'dashboard');
  return `${CRM_FRAME_PATH}?${params.toString()}`;
}

export function postToCrm(frame: HTMLIFrameElement | null, message: ConsoleToCrm): boolean {
  const win = frame?.contentWindow;
  if (!win) return false;
  try {
    win.postMessage({ source: CONSOLE_SOURCE, ...message }, window.location.origin);
    return true;
  } catch {
    return false;
  }
}

const str = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : String(v));

const MEDIA_TYPES = new Set(['photo', 'video', 'audio', 'gif']);
/** More than any message or post takes; the rest is ignored. */
const MAX_FRAME_PICKS = 200;

function framePicked(raw: unknown): FramePickedMedia[] {
  if (!raw || typeof raw !== 'object') return [];
  const row = raw as Record<string, unknown>;
  const id = Number(row.id);
  if (!Number.isSafeInteger(id) || id <= 0) return [];
  const type = str(row.type).toLowerCase();
  return [{ id, type: (MEDIA_TYPES.has(type) ? type : 'photo') as FramePickedMedia['type'], thumb: str(row.thumb) }];
}

/** Validate an incoming message: same origin, from our frame, known shape. */
export function parseCrmMessage(event: MessageEvent, frame: HTMLIFrameElement | null): CrmToConsole | null {
  if (event.origin !== window.location.origin) return null;
  if (!frame?.contentWindow || event.source !== frame.contentWindow) return null;
  const data = event.data as Record<string, unknown> | null;
  if (!data || typeof data !== 'object' || data.source !== CRM_SOURCE || typeof data.type !== 'string') return null;
  switch (data.type) {
    case 'crm:ready':
    case 'crm:signed-out':
      return { type: data.type };
    case 'crm:location':
      return { type: 'crm:location', accountId: str(data.accountId), page: str(data.page) };
    case 'crm:workspace':
      return { type: 'crm:workspace', accountId: str(data.accountId), mode: str(data.mode), message: str(data.message) };
    case 'crm:auth-expired':
      return { type: 'crm:auth-expired', reason: str(data.reason) };
    case 'crm:unread': {
      const count = Number(data.count);
      return { type: 'crm:unread', accountId: str(data.accountId), count: Number.isFinite(count) ? Math.max(0, count) : 0 };
    }
    case 'crm:access-denied':
      return { type: 'crm:access-denied', accountId: str(data.accountId) };
    case 'crm:account-session': {
      const accountId = str(data.accountId);
      return accountId ? { type: 'crm:account-session', accountId, loggedOut: data.loggedOut === true } : null;
    }
    case 'crm:pick-content': {
      const requestId = str(data.requestId).slice(0, 64);
      const accountId = str(data.accountId);
      if (!requestId || !accountId) return null;
      const initial = Array.isArray(data.initial) ? data.initial.slice(0, MAX_FRAME_PICKS).flatMap(framePicked) : [];
      return { type: 'crm:pick-content', requestId, accountId, initial };
    }
    case 'crm:no-access': {
      const status = Number(data.status);
      return { type: 'crm:no-access', status: Number.isFinite(status) ? status : 0 };
    }
    default:
      return null;
  }
}

/** A picked item in the vault/media row shape the frame's pages already read. */
export function toFrameRow(m: VaultItem): FrameVaultRow {
  return {
    id: m.id,
    type: m.type,
    duration: m.duration,
    createdAt: m.createdAt,
    files: { thumb: { url: m.small || m.thumb }, squarePreview: { url: m.thumb }, full: { url: m.full } },
  };
}
